import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Post from '../../components/post/Post';
import {
  SHARE_FUNNEL_EVENT_NAMES,
  appendShareFunnelParams,
  buildShareFunnelContext,
  recordShareFunnelEvent,
} from '../../utils/localOpsShareFunnel';
import './shareLanding.css';

const resolveRouteTarget = (params, pathname) => {
  if (params?.type && params?.id) {
    return {
      type: params.type,
      id: params.id,
    };
  }

  if (params?.taskId) {
    return {
      type: 'task',
      id: params.taskId,
    };
  }

  if (params?.offeringId) {
    return {
      type: 'offering',
      id: params.offeringId,
    };
  }

  const pathParts = String(pathname || '')
    .split('/')
    .filter(Boolean);

  if (pathParts[0] === 'share' && pathParts[1] && pathParts[2]) {
    return {
      type: pathParts[1],
      id: pathParts[2],
    };
  }

  if (pathParts[0] === 'tasks' && pathParts[1]) {
    return {
      type: 'task',
      id: pathParts[1],
    };
  }

  if (pathParts[0] === 'offerings' && pathParts[1]) {
    return {
      type: 'offering',
      id: pathParts[1],
    };
  }

  return {
    type: null,
    id: null,
  };
};

export default function ShareLanding() {
  const params = useParams();
  const routeTarget = useMemo(
    () => resolveRouteTarget(params, window.location.pathname),
    [params],
  );
  const type = routeTarget.type;
  const id = routeTarget.id;
  const [shareData, setShareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const shareFunnelContext = useMemo(() => {
    if (!type || !id) return null;
    return buildShareFunnelContext({
      share: `/share/${type}/${id}`,
      queryParams:
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search || '')
          : new URLSearchParams(),
    });
  }, [type, id]);

  useEffect(() => {
    const prevBodyBg = document.body.style.background;
    const prevHtmlBg = document.documentElement.style.background;
    document.body.style.background = '#050912';
    document.documentElement.style.background = '#050912';
    return () => {
      document.body.style.background = prevBodyBg;
      document.documentElement.style.background = prevHtmlBg;
    };
  }, []);

  useEffect(() => {
    if (!type || !id) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    let cancelled = false;

    setLoading(true);
    setNotFound(false);

    const stripApi = (url) =>
      typeof url === 'string' ? url.replace(/\/api\/v1\/?$/, '') : '';
    const backendOrigin =
      stripApi(process.env.REACT_APP_AZURE_API_BASE_URL) ||
      stripApi(process.env.REACT_APP_API_BASE_URL_PROD) ||
      stripApi(process.env.REACT_APP_API_BASE_URL) ||
      'http://localhost:4000';

    fetch(`${backendOrigin}/share/resolve/${type}/${id}`, {
      headers: { Accept: 'application/json' },
    })
      .then((res) => {
        if (!res.ok) throw new Error('not-found');
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        setShareData(json?.data || null);
        setNotFound(!json?.data);
      })
      .catch(() => {
        if (cancelled) return;
        setNotFound(true);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [type, id]);

  useEffect(() => {
    if (!shareData || !shareFunnelContext) return;
    recordShareFunnelEvent(
      SHARE_FUNNEL_EVENT_NAMES.LANDING_VIEWED,
      shareFunnelContext,
    );
  }, [shareData, shareFunnelContext]);

  const initialPost = useMemo(() => {
    if (!shareData) return null;
    const entityType =
      shareData.type ||
      shareData.entityType ||
      (type === 'offering' ? 'offering' : 'task');
    const ownerHandle =
      (shareData.actorLabel && String(shareData.actorLabel).trim()) || 'LocalOps';
    const regionLabel = shareData.regionLabel || shareData.contextLabel || '';
    const isOffering = entityType === 'offering';
    const cover = shareData?.mediaUrl || shareData?.fallbackImage || null;
    const media = cover ? [{ url: cover, type: 'image' }] : [];
    const parseNumber = (val) => {
      const num = Number(val);
      return Number.isFinite(num) ? num : null;
    };
    const rate =
      parseNumber(shareData.jgkPerUsd) && parseNumber(shareData.jgkPerUsd) > 0
        ? parseNumber(shareData.jgkPerUsd)
        : 1000;

    const priceUsd = isOffering
      ? (() => {
          const amt = parseNumber(shareData.amount);
          if (Number.isFinite(amt) && amt > 0) return amt;
          const priceFields = [
            parseNumber(shareData.offering?.price),
            parseNumber(shareData.offering?.priceUsd),
            parseNumber(shareData.offering?.price_usd),
          ];
          const firstPrice = priceFields.find((v) => Number.isFinite(v) && v > 0);
          if (Number.isFinite(firstPrice)) return firstPrice;
          const hookMatch = /[$]\s*([0-9]+(?:\.[0-9]{1,2})?)/.exec(
            shareData.hookText || '',
          );
          if (hookMatch) {
            const parsed = parseNumber(hookMatch[1]);
            if (Number.isFinite(parsed) && parsed > 0) return parsed;
          }
          return null;
        })()
      : (() => {
          const candidates = [
            parseNumber(shareData.providerPayoutAmount),
            parseNumber(shareData.task?.price),
            parseNumber(shareData.task?.priceUsd),
            parseNumber(shareData.task?.price_usd),
          ];
          const firstUsd = candidates.find(
            (v) => Number.isFinite(v) && v > 0,
          );
          if (Number.isFinite(firstUsd)) return firstUsd;
          // Fallback: parse from hookText like "Paid today · $25"
          const hookMatch = /[$]\s*([0-9]+(?:\.[0-9]{1,2})?)/.exec(
            shareData.hookText || '',
          );
          if (hookMatch) {
            const parsed = parseNumber(hookMatch[1]);
            if (Number.isFinite(parsed) && parsed > 0) return parsed;
          }
          const requiredJgk =
            parseNumber(shareData.task?.requiredJGK) ||
            parseNumber(shareData.task?.requiredJgk) ||
            parseNumber(shareData.requiredJGK) ||
            parseNumber(shareData.requiredJgk);
          if (Number.isFinite(requiredJgk) && requiredJgk > 0) {
            return requiredJgk / rate;
          }
          return null;
        })();
    const priceJgk =
      parseNumber(shareData.task?.requiredJGK) ||
      parseNumber(shareData.task?.requiredJgk) ||
      parseNumber(shareData.requiredJGK) ||
      parseNumber(shareData.requiredJgk) ||
      (Number.isFinite(priceUsd) ? Math.round(priceUsd * rate) : null);
    const escrowStatus = isOffering ? 'payment_ready' : 'held';
    const baseHook = shareData.hookText || (isOffering ? 'Price' : 'Paid today');
    // If the resolver already provided hookText, treat it as final (no client-side price re-append)
    const hookText =
      shareData.hookText ||
      (priceUsd && priceUsd > 0 ? `${baseHook}: $${priceUsd.toFixed(2)}` : baseHook);
    const priceUsdForPost = priceUsd;
    const priceJgkForPost = priceJgk;
    const resolvedTitle =
      shareData.title ||
      shareData.task?.title ||
      shareData.offering?.title ||
      'Local opportunity';
    const resolvedDesc =
      shareData.description ||
      shareData.task?.description ||
      shareData.offering?.description ||
      '';
    return {
      _id: shareData.id || `${type}-${id}`,
      title: resolvedTitle,
      desc: resolvedDesc,
      playerId: ownerHandle,
      location: regionLabel
        ? {
            address: regionLabel,
          }
        : undefined,
      media: shareData.mediaUrl
        ? [
            {
              url: shareData.mediaUrl,
              type: 'image',
            },
          ]
        : [],
      coverIndex: 0,
      context: `localops.${entityType === 'offering' ? 'offering' : 'task'}`,
      meta: {
        localops: {
          entityType,
          shareUrl: `/share/${type}/${id}`,
          continueUrl: appendShareFunnelParams(
            shareData.continuationUrl,
            shareFunnelContext,
          ),
          shareFunnelContext,
          hookText,
          regionLabel,
          priceUsd: priceUsdForPost,
          priceJGK: priceJgkForPost,
          escrowStatus,
          owner: { playerId: ownerHandle, displayName: ownerHandle },
          task: isOffering
            ? undefined
            : {
                title: resolvedTitle,
                description: resolvedDesc,
              },
          offering: isOffering
            ? {
                title: resolvedTitle,
                description: resolvedDesc,
                requestQuote: false,
              }
            : undefined,
          status: 'discoverable',
        },
      },
    };
  }, [shareData, type, id, shareFunnelContext]);

  if (loading) {
    return (
      <div className="share-landing">
        <div className="share-landing__card">Loading...</div>
      </div>
    );
  }

  if (notFound || !shareData) {
    return (
      <div className="share-landing">
        <div className="share-landing__card">
          <div className="share-landing__brand">JG LocalOps</div>
          <h2>{type === 'offering' ? 'Offering not found' : 'Task not found'}</h2>
          <p>This item is no longer available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="share-landing" style={{ background: '#050912' }}>
      <div className="share-landing__card share-landing__card--post">
        {initialPost ? (
          <Post
            key={shareData?.id || `${type}-${id}`}
            initialPost={initialPost}
            isShareLanding
            onLocalOpsPrimaryCta={() =>
              recordShareFunnelEvent(
                SHARE_FUNNEL_EVENT_NAMES.ACCEPT_CLICKED,
                shareFunnelContext,
              )
            }
            primaryCtaOverride={
              shareData.primaryCta ||
              (shareData.type === 'offering' ? 'Reserve' : 'Accept')
            }
          />
        ) : null}
      </div>
    </div>
  );
}
